import { useState, useRef, useEffect } from 'react';
import { ChevronDownIcon, MagnifyingGlassIcon, XMarkIcon } from '@heroicons/react/24/outline';

const Dropdown = ({ 
  value, 
  onChange, 
  options = [], 
  placeholder = 'Select...', 
  className = '',
  disabled = false,
  icon: Icon = null,
  searchable = false,
  clearable = false,
  loading = false,
  error = null,
  renderOption = null,
  renderSelected = null,
  grouped = false,
  maxHeight = 'max-h-60',
  dropdownClassName = '',
  optionClassName = '',
  size = 'md'
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const dropdownRef = useRef(null);
  const searchInputRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchQuery('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (isOpen && searchable && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpen, searchable]);

  const selectedOption = options.find(opt => opt.value === value);

  const filteredOptions = searchable && searchQuery
    ? options.filter(opt => 
        opt.label.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (opt.description && opt.description.toLowerCase().includes(searchQuery.toLowerCase()))
      )
    : options;

  const handleClear = (e) => {
    e.stopPropagation();
    onChange(null);
  };

  const handleSelect = (optionValue) => {
    onChange(optionValue);
    setIsOpen(false);
    setSearchQuery('');
  };

  const sizeClasses = {
    sm: 'px-3 py-1.5 text-xs',
    md: 'px-4 py-2 text-sm',
    lg: 'px-5 py-3 text-base'
  };

  const renderOptionContent = (option) => {
    if (renderOption) {
      return renderOption(option);
    }

    return (
      <div className="flex items-center gap-2">
        {option.icon && <option.icon className="w-4 h-4 flex-shrink-0" />}
        <div className="flex-1 min-w-0">
          <div className="truncate">{option.label}</div>
          {option.description && (
            <div className="text-xs text-gray-500 truncate">{option.description}</div>
          )}
        </div>
        {option.badge && (
          <span className="px-2 py-0.5 text-xs rounded-full bg-gray-100 text-gray-600">
            {option.badge}
          </span>
        )}
      </div>
    );
  };

  const renderSelectedContent = () => {
    if (renderSelected && selectedOption) {
      return renderSelected(selectedOption);
    }

    if (selectedOption) {
      return (
        <div className="flex items-center gap-2 flex-1 min-w-0">
          {selectedOption.icon && <selectedOption.icon className="w-4 h-4 flex-shrink-0" />}
          <span className="truncate">{selectedOption.label}</span>
        </div>
      );
    }

    return <span className="text-gray-500">{placeholder}</span>;
  };

  const renderGroupedOptions = () => {
    const groups = {};
    filteredOptions.forEach(option => {
      const group = option.group || 'Other';
      if (!groups[group]) groups[group] = [];
      groups[group].push(option);
    });

    return Object.entries(groups).map(([groupName, groupOptions]) => (
      <div key={groupName}>
        <div className="px-4 py-2 text-xs font-semibold text-gray-500 uppercase bg-gray-50 sticky top-0">
          {groupName}
        </div>
        {groupOptions.map(option => renderOptionItem(option))}
      </div>
    ));
  };

  const renderOptionItem = (option) => (
    <button
      key={option.value}
      type="button"
      onClick={() => handleSelect(option.value)}
      disabled={option.disabled}
      className={`
        w-full px-4 py-2.5 text-left hover:bg-blue-50 
        transition-colors border-b border-gray-100 last:border-b-0
        ${value === option.value ? 'bg-blue-50 text-blue-700 font-medium' : 'text-gray-700'}
        ${option.disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
        ${optionClassName}
      `}
    >
      {renderOptionContent(option)}
    </button>
  );

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      <button
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        className={`
          flex items-center justify-between gap-2 w-full
          bg-white border rounded-lg font-medium 
          hover:bg-gray-50 hover:border-gray-400 
          focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 
          transition-colors
          ${error ? 'border-red-300' : 'border-gray-300'}
          ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
          ${sizeClasses[size]}
        `}
      >
        <div className="flex items-center gap-2 flex-1 min-w-0">
          {Icon && <Icon className="w-4 h-4 text-gray-500 flex-shrink-0" />}
          {loading ? (
            <span className="text-gray-500">Loading...</span>
          ) : (
            renderSelectedContent()
          )}
        </div>
        <div className="flex items-center gap-1 flex-shrink-0">
          {clearable && value && !disabled && (
            <XMarkIcon 
              className="w-4 h-4 text-gray-400 hover:text-gray-600" 
              onClick={handleClear}
            />
          )}
          <ChevronDownIcon 
            className={`w-4 h-4 text-gray-500 transition-transform ${isOpen ? 'rotate-180' : ''}`} 
          />
        </div>
      </button>

      {error && (
        <p className="mt-1 text-xs text-red-600">{error}</p>
      )}

      {isOpen && (
        <div className={`absolute z-50 mt-2 w-full bg-white border border-gray-200 rounded-lg shadow-xl ${maxHeight} overflow-hidden flex flex-col ${dropdownClassName}`}>
          {searchable && (
            <div className="p-2 border-b border-gray-200 sticky top-0 bg-white">
              <div className="relative">
                <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  ref={searchInputRef}
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search..."
                  className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
          )}
          
          <div className="overflow-y-auto">
            {loading ? (
              <div className="px-4 py-8 text-center text-gray-500">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500 mx-auto"></div>
                <p className="mt-2 text-sm">Loading...</p>
              </div>
            ) : filteredOptions.length === 0 ? (
              <div className="px-4 py-8 text-center text-gray-500 text-sm">
                {searchQuery ? 'No results found' : 'No options available'}
              </div>
            ) : grouped ? (
              renderGroupedOptions()
            ) : (
              filteredOptions.map(option => renderOptionItem(option))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Dropdown;
